// Phase 3B — client hook binding path memory to the return-loop model.
//
// SSR-safe: renders a stable "first visit, nothing remembered" state during SSR
// and the first paint, so nothing shifts when device memory resolves.
import { useCallback, useEffect, useState } from "react";
import { useJourney } from "@/hooks/use-journey";
import {
  conversionPromptDecision,
  pickContinuation,
  resolveReturnState,
  type PathMemoryEntry,
  type PromptDecision,
  type ReturnState,
} from "@/lib/return-loop";
import {
  markVisitDay,
  readMemory,
  readStats,
  recordPromptDismissed,
  recordPromptShown,
  subscribeMemory,
  type PathStats,
} from "@/lib/path-memory";
import { track } from "@/lib/analytics";

const SESSION_PROMPT_KEY = "ka.path.promptShown.v1";
const SESSION_DAY_KEY = "ka.path.dayLogged.v1";

export interface ReturnLoopState {
  ready: boolean;
  state: ReturnState;
  memory: PathMemoryEntry[];
  stats: PathStats;
  visitDays: number;
  /** The single best thing to resume, if any. */
  continuation: (currentHref?: string) => PathMemoryEntry | null;
  promptDecision: PromptDecision;
  markPromptShown: () => void;
  markPromptDismissed: () => void;
}

const EMPTY_STATS: PathStats = {
  days: [],
  startedCount: 0,
  valueCount: 0,
  lastPromptAt: null,
  lastDismissedAt: null,
};

export function useReturnLoop(): ReturnLoopState {
  const journey = useJourney();
  const [ready, setReady] = useState(false);
  const [memory, setMemory] = useState<PathMemoryEntry[]>([]);
  const [stats, setStats] = useState<PathStats>(EMPTY_STATS);
  const [visitDays, setVisitDays] = useState(1);
  const [promptShownSession, setPromptShownSession] = useState(false);

  useEffect(() => {
    const days = markVisitDay();
    setVisitDays(days);
    setMemory(readMemory());
    setStats(readStats());
    setReady(true);
    try {
      setPromptShownSession(window.sessionStorage.getItem(SESSION_PROMPT_KEY) === "1");
      if (days > 1 && window.sessionStorage.getItem(SESSION_DAY_KEY) !== "1") {
        window.sessionStorage.setItem(SESSION_DAY_KEY, "1");
        track("return_visit_day", { day_count: days });
      }
    } catch {
      /* private mode */
    }
    return subscribeMemory(() => {
      setMemory(readMemory());
      setStats(readStats());
    });
  }, []);

  const state = resolveReturnState({
    signedIn: journey.signedIn,
    isMember: journey.isMember,
    visitDays,
    startedCount: stats.startedCount,
    valueCount: stats.valueCount,
  });

  const continuation = useCallback(
    (currentHref?: string) => pickContinuation(memory, { currentHref }),
    [memory],
  );

  const promptDecision = conversionPromptDecision({
    state,
    startedCount: stats.startedCount,
    valueCount: stats.valueCount,
    lastPromptAt: stats.lastPromptAt,
    lastDismissedAt: stats.lastDismissedAt,
    shownThisSession: promptShownSession,
  });

  const markPromptShown = useCallback(() => {
    setPromptShownSession(true);
    try {
      window.sessionStorage.setItem(SESSION_PROMPT_KEY, "1");
    } catch {
      /* private mode */
    }
    recordPromptShown();
  }, []);

  const markPromptDismissed = useCallback(() => {
    setPromptShownSession(true);
    try {
      window.sessionStorage.setItem(SESSION_PROMPT_KEY, "1");
    } catch {
      /* private mode */
    }
    recordPromptDismissed();
  }, []);

  return {
    ready: ready && !journey.loading,
    state,
    memory,
    stats,
    visitDays,
    continuation,
    promptDecision,
    markPromptShown,
    markPromptDismissed,
  };
}
